<template>
  <div class="agent-daily-revenue">
    <!-- 筛选条件 -->
    <el-card shadow="never" class="filter-card">
      <el-form :inline="true" :model="form" @submit.native.prevent>
        <div style="display:flex;flex-wrap:wrap;align-items:center;gap:8px;">
          <el-form-item label="时间范围" class="date-range-item">
            <el-date-picker
              v-model="form.dateRange"
              type="daterange"
              range-separator="-"
              start-placeholder="开始日期"
              end-placeholder="结束日期"
              format="yyyy-MM-dd"
              value-format="yyyy-MM-dd"
              :clearable="false"
              style="width:280px;"
            />
          </el-form-item>
          <el-form-item>
            <el-button type="primary" :loading="loading" @click="handleSearch" icon="el-icon-search">查询</el-button>
            <el-button type="info" plain @click="handleReset" :disabled="loading" icon="el-icon-refresh">重置</el-button>
          </el-form-item>
          <div class="balance-badge">
            <span class="balance-label">累计收益</span>
            <span class="balance-value">{{ formattedBalance }}</span>
          </div>
        </div>
      </el-form>
    </el-card>

    <!-- 数据表格 -->
    <el-card shadow="never" class="table-card">
      <div v-if="loading" class="loading-state">
        <i class="el-icon-loading"></i> 加载中...
      </div>
      <el-empty v-else-if="!dailyList.length" description="所选时间范围内暂无收益数据" />
      <div v-else>
        <!-- 日历视图 -->
        <div class="calendar-grid">
          <div
            v-for="item in dailyList"
            :key="item.date"
            class="calendar-cell"
            :class="{ 'is-today': item.date === todayStr }"
          >
            <div class="cal-date">{{ formatDateShort(item.date) }}</div>
            <div class="cal-commission">¥{{ formatMoney(item.commission) }}</div>
          </div>
        </div>
      </div>
    </el-card>
  </div>
</template>

<script>
import { getAgentDailyRevenue, getMyBalance } from '@/api/dashboard'

function pad2(n) {
  return String(n).padStart(2, '0')
}

function getDefaultDateRange() {
  const now = new Date()
  const end = formatDate(now)
  const start = new Date(now)
  start.setDate(start.getDate() - 13)
  return [formatDate(start), end]
}

function formatDate(date) {
  return [date.getFullYear(), pad2(date.getMonth() + 1), pad2(date.getDate())].join('-')
}

export default {
  name: 'AgentDailyRevenue',
  data() {
    return {
      form: {
        dateRange: getDefaultDateRange()
      },
      loading: false,
      dailyList: [],
      todayStr: formatDate(new Date()),
      balance: 0
    }
  },
  computed: {
    formattedBalance() {
      const v = (this.balance || 0) / 1000
      if (v >= 10000) return (v / 10000).toFixed(2) + '万'
      return v.toFixed(2)
    }
  },
  mounted() {
    this.handleSearch()
    this.fetchBalance()
  },
  methods: {
    formatMoney(val) {
      const v = (val || 0) / 1000
      if (v >= 10000) return (v / 10000).toFixed(2) + '万'
      return v.toFixed(2)
    },
    formatDateShort(dateStr) {
      if (!dateStr) return ''
      const parts = dateStr.split('-')
      if (parts.length < 3) return dateStr
      return parseInt(parts[1], 10) + '/' + parseInt(parts[2], 10)
    },
    async handleSearch() {
      const [start, end] = this.form.dateRange || []
      if (!start || !end) return
      this.loading = true
      try {
        this.dailyList = await getAgentDailyRevenue(start, end)
      } catch (e) {
        console.error('获取团长每日收益失败:', e)
        this.dailyList = []
      } finally {
        this.loading = false
      }
    },
    handleReset() {
      this.form.dateRange = getDefaultDateRange()
      this.handleSearch()
    },
    async fetchBalance() {
      try {
        this.balance = await getMyBalance()
      } catch (e) {
        console.error('获取累计金币失败:', e)
      }
    }
  }
}
</script>

<style scoped>
.agent-daily-revenue {
  padding: 0;
}

/* ===== 筛选卡片 ===== */
.filter-card {
  margin-bottom: 16px;
}

.date-range-item {
  position: relative;
}

/* ===== 累计金币徽章 ===== */
.balance-badge {
  margin-left: auto;
  display: flex;
  align-items: center;
  gap: 8px;
  background: linear-gradient(135deg, #fef6e0 0%, #fff7ed 100%);
  border: 1px solid #fde68a;
  border-radius: 8px;
  padding: 6px 16px;
}

.balance-label {
  font-size: 13px;
  color: #92400e;
  font-weight: 500;
}

.balance-value {
  font-size: 18px;
  font-weight: 700;
  color: #d97706;
  font-family: 'Arial', sans-serif;
  letter-spacing: 0.02em;
}

/* ===== 日历网格 ===== */
.calendar-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(180px, 1fr));
  gap: 12px;
}

.calendar-cell {
  background: #fafbfd;
  border: 1px solid #e3e5ec;
  border-radius: 10px;
  padding: 16px 14px;
  text-align: center;
  transition: all 0.25s ease;
}

.calendar-cell:hover {
  border-color: #5865f2;
  box-shadow: 0 4px 16px rgba(88, 101, 242, 0.12);
  transform: translateY(-2px);
}

.calendar-cell.is-today {
  border-color: #5865f2;
  background: linear-gradient(135deg, rgba(88, 101, 242, 0.06) 0%, rgba(124, 58, 237, 0.04) 100%);
  box-shadow: 0 0 0 2px rgba(88, 101, 242, 0.15);
}

.cal-date {
  font-size: 13px;
  color: #64748b;
  margin-bottom: 8px;
  font-weight: 500;
}

.calendar-cell.is-today .cal-date {
  color: #5865f2;
  font-weight: 600;
}

.cal-commission {
  font-size: 22px;
  font-weight: 700;
  color: #5865f2;
  margin-bottom: 0;
  font-family: 'Arial', sans-serif;
}

/* ===== 加载状态 ===== */
.loading-state {
  text-align: center;
  padding: 40px 0;
  color: #94a3b8;
  font-size: 14px;
}

.loading-state i {
  margin-right: 6px;
  font-size: 18px;
}

/* ===== 响应式 ===== */
@media (max-width: 767px) {
  .calendar-grid {
    grid-template-columns: repeat(auto-fill, minmax(150px, 1fr));
    gap: 8px;
  }
}
</style>
